import { Injectable } from "@angular/core";
import { ITab } from "./i-tab";
import { SaveMode } from "./save-mode.enum";
import { LocalStorageService } from "../caching/local-storage.service";
import { SessionStorageService } from "../caching/session-storage.service";

@Injectable({
  providedIn: "root"
})
export class TabCacheService {

  tabs: ITab[] = [];

  mode = SaveMode.Session;

  private volatileKeys: string[] = [];

  private readonly KEY = "TABS";

  activeSessionKeys: string[] = [];

  constructor(
    private _localStorageService: LocalStorageService,
    private _sessionStorageService: SessionStorageService
  ) {}

  getCachedTabs() {
    return this.tabs;
  }

  clearTabs() {
    this.tabs = [];
  }

  storeTab(tab) {
    this.tabs.push(tab);
  }

  loadTabsFromStorage(): ITab[] {
    switch (this.mode) {
      case SaveMode.Local:
        return this._localStorageService.get<ITab[]>(this.KEY);
      case SaveMode.Session:
        return this._sessionStorageService.get<ITab[]>(this.KEY);
      default:
        return null;
    }
  }

  save(tabs: ITab[]) {
    switch (this.mode) {
      case SaveMode.Local:
        this._localStorageService.set(this.KEY, tabs);
        break;

      case SaveMode.Session:
        this._sessionStorageService.set(this.KEY, tabs);
        break;
    }
  }

  getTabKey(tabId: string) {
    return `TAB${tabId}`;
  }

  getTabSession<T>(tabId: string): T {
    const key = this.getTabKey(tabId);

    return this._sessionStorageService.get<T>(key);
  }

  updateTabSession(tabId: string, value: object) {
    const key = this.getTabKey(tabId);

    this._sessionStorageService.set(key, value);
    this.activeSessionKeys.push(key);
  }

  removeTabSession(tabId: string) {
    const key = this.getTabKey(tabId);

    this._sessionStorageService.delete(key);
    const index = this.activeSessionKeys.indexOf(key);
    if (index !== -1) {
      this.activeSessionKeys.splice(index, 1);
    }
  }

  clearAllTabSessions() {
    for (const session of this.activeSessionKeys) {
      this._sessionStorageService.delete(session);
    }
    this.activeSessionKeys = [];
  }


  /**
   * Get cached value
   * @param key part one of the key
   * @param tabId part two of the key
   */
  getVolatileCache<T = any>(key: string, tabId: string) {
    if (this.volatileKeys.includes(key + tabId)) {
      return this._sessionStorageService.get<T>(key + tabId);
    }
    return null;
  }

  /**
   *
   * @param key part one of the composite key
   * @param tabId part two of the composite key
   * @param value value to be stored
   */
  updateVolatileCache(key: string, tabId: string, value: any) {
    if (!this.volatileKeys.includes(key + tabId)) {
      this.volatileKeys.push(key + tabId);
    }
    this._sessionStorageService.set(key + tabId, value);
  }

  /**
   * used to invalidate a cache with a tab ID
   * @param tabId The ID to be used to find all keys to be deleted
   */
  removeTabVolatileCache(tabId: string) {
    this.volatileKeys = this.volatileKeys.filter(x => {
      if (x.endsWith(tabId)) {
        this._sessionStorageService.delete(x);
        return false;
      }
      return true;
    });
  }

  /**
   * Used to invalidate caches associated with a key
   * @param key The first part of the composite key to be removed
   */
  removeKeyVolatileCache(key: string) {
    this.volatileKeys = this.volatileKeys.filter(x => {
      if (x.startsWith(key)) {
        this._sessionStorageService.delete(x);
        return false;
      }
      return true;
    });
  }

  registerTabSession(tabId, session) {
    this.updateTabSession(tabId, session);
  }

  invalidateCachedValue(key) {
    this.removeKeyVolatileCache(key);
  }

  tabExists(tab: any) {
    return this.tabs.find(t => t.id === tab.id);
  }

  getTabById(tabId) {
    return this.tabs.find(t => t.id === tabId);
  }
}
